import {
  browserName,
  browserVersion,
  deviceType,
  getUA,
  osName,
  osVersion,
} from 'react-device-detect';

import { AppDefaults } from 'config/global-enums';
import { message } from 'antd';
import { removeHttpHttpsWww } from 'utils/tools/data-transformers';
import { DOMAIN } from 'config/api-variables';

export const DEVICE_CODE = 'deviceCode';
export const DEVICE_DETAILS = 'deviceDetails';
export const TOKEN_KEY = 'userToken';
export const APP_TYPE = 'appType';
export const VIRTUAL_TOKEN_KEY = 'virtualToken';
export const FCM_ID = 'fcmId';
export const VERIFY_CREDENTIALS = 'verifyFoopCredentials';
export const VAPIDKEY =
  'BOWdlEHTiTXNl4ncjBd3URkD5cPtkoSDpLKmq1kTzmTFhU6zHo0gk22jti9_agP4IPGl49m3nNb5dIk1AezACZk';
export const IS_DEMO_FORM_FILLED = 'isDemoFormFilled';
export const TEST_CUSTOMER_TOKEN_KEY = 'testCustomerToken';

// Hard reset for newer versions
export const clearCacheAndHardReload = () => {
  const verVal = getLocalStorage('lastVersion');
  if (!verVal || verVal !== AppDefaults.APP_VERSION + AppDefaults.APP_BUILD) {
    setLocalStorage(
      'lastVersion',
      AppDefaults.APP_VERSION + AppDefaults.APP_BUILD,
    );
    if (caches) {
      caches.keys().then(names => names.forEach(name => caches.delete(name)));
    }
    window.location.reload(true);
  }
};

export const getHostName = window.location.hostname?.replace(
  /(test.)?(beta.)?(www.)?/,
  '',
);
const subDomainName = getHostName.split('.')[0];

const cookieNameGenerator = name =>
  (process.env.REACT_APP_ENV === 'test' ? 'beta_' : '') +
  (removeHttpHttpsWww(window.location.hostname) !== removeHttpHttpsWww(DOMAIN)
    ? `${subDomainName ? subDomainName : 'cleandesk'}_`
    : 'cleandeskai_') +
  name;

export const changeCookiesName = (redirectDomain, pathName) => {
  const redirectUrl = `https://${redirectDomain}${pathName}`;
  const SubDomainName = redirectDomain.split('.')[0];

  const cookies = document.cookie.split(';');

  const expiration = new Date(
    Date.now() + 30 * 24 * 60 * 60 * 1000,
  ).toUTCString();

  for (let i = 0; i < cookies.length; i++) {
    // let matches = cookies[i].match(/beta_([^_]+)_/);
    let matches;
    if (process.env.REACT_APP_ENV === 'test') {
      matches = cookies[i].match(/beta_([^_]+)_/);
    } else {
      matches = cookies[i].match(/([^_]+)_/);
    }
    if (matches && matches.length > 1) {
      let oldWord = matches[1];
      let newWord = SubDomainName;
      cookies[i] = cookies[i].replace(oldWord, newWord);
    }

    // Append the expiration date to the cookie string
    cookies[i] += `; expires= ${expiration};` + getCookieExtraParameters;

    // Set the cookie
    document.cookie = cookies[i];
  }

  removeCookie(APP_TYPE);
  logoutClearLocal();

  window.location.replace(redirectUrl);
};

const getCookieExtraParameters = ` path=/; SameSite=None; Secure; domain=${
  process.env.NODE_ENV === 'development'
    ? 'localhost'
    : window.location.hostname.includes('cleandesk.ai')
    ? '.cleandesk.ai'
    : '.cleandesk.co.in'
}`;

export const setCookie = (name, value) => {
  if (navigator.cookieEnabled) {
    const expiration = new Date(
      Date.now() + 30 * 24 * 60 * 60 * 1000,
    ).toUTCString();
    document.cookie =
      `${cookieNameGenerator(name)}=${value}; expires=${expiration};` +
      getCookieExtraParameters;
    return;
  }
  setLocalStorage(name, value);
};

export const getCookie = name => {
  if (navigator.cookieEnabled) {
    let matches = document.cookie.match(
      new RegExp(
        '(?:^|; )' +
          cookieNameGenerator(name).replace(
            // eslint-disable-next-line no-useless-escape
            /([\.$?*|{}\(\)\[\]\\\/\+^])/g,
            '\\$1',
          ) +
          '=([^;]*)',
      ),
    );
    return matches ? decodeURIComponent(matches[1]) : getLocalStorage(name);
  }
  return getLocalStorage(name);
};

export const removeCookie = name => {
  if (navigator.cookieEnabled) {
    const expiration = new Date(Date.now() - 60 * 60 * 1000).toUTCString();
    document.cookie =
      `${cookieNameGenerator(name)}=; expires=${expiration};` +
      getCookieExtraParameters;
    return;
  }
  removeLocalStorage(name);
};

export const setLocalStorage = (name, value) => {
  try {
    localStorage.setItem(name, value);
  } catch (e) {
    message.error('Unable to set value in local storage: ', e);
  }
};

export const getLocalStorage = name => {
  try {
    return localStorage.getItem(name);
  } catch (e) {
    message.error('Unable to get value from local storage: ', e);
  }
};

export const removeLocalStorage = name => {
  try {
    localStorage.removeItem(name);
  } catch (e) {
    message.error('Unable to remove value from local storage: ', e);
  }
};

export const setAuthKey = token => setCookie(TOKEN_KEY, token);

export const getAuthKey = () => getCookie(TOKEN_KEY);

export const setUserType = value => setCookie(APP_TYPE, value);

export const getUserType = () => getCookie(APP_TYPE);

export const setIsDemoFormFilled = value =>
  setCookie(IS_DEMO_FORM_FILLED, value);

export const getIsDemoFormFilled = () => getCookie(IS_DEMO_FORM_FILLED);

export const setTestCustomerToken = token =>
  setCookie(TEST_CUSTOMER_TOKEN_KEY, token);

export const getTestCustomerToken = () => getCookie(TEST_CUSTOMER_TOKEN_KEY);

export const setFCMid = token => {
  let prevDeviceData =
    getDeviceDetails()?.devicedetails &&
    JSON.parse(getDeviceDetails()?.devicedetails);
  if (prevDeviceData) {
    prevDeviceData = { ...prevDeviceData, fcm_id: token };
    const stringifiedDeviceData = JSON.stringify(prevDeviceData);
    setLocalStorage(DEVICE_DETAILS, stringifiedDeviceData);
  }
  setCookie(FCM_ID, token);
};

export const getFCMid = () => getCookie(FCM_ID) || '';

export const extendCookieExpiration = () => {
  const authToken = getAuthKey(),
    userType = getUserType(),
    fcmToken = getFCMid(),
    deviceCode = getCookie(DEVICE_CODE),
    testCustomerToken = getTestCustomerToken();

  setAuthKey(authToken);
  setUserType(userType);
  setFCMid(fcmToken);
  setCookie(DEVICE_CODE, deviceCode);
  setTestCustomerToken(testCustomerToken);
};

export const resetDeviceDetails = () => {
  const oldDeviceData = getDeviceDetails();

  if (oldDeviceData) {
    return oldDeviceData;
  }

  const uniqueDeviceCode = new Date().getTime().toString();

  const deviceData = {
    fcm_id: getFCMid(),
    app_type: getUserType(),
    device_info: deviceType,
    device_type: deviceType,
    device_vendor: deviceType,
    mac_id: uniqueDeviceCode,
    browser_type: browserName,
    browser_version: browserVersion,
    os_type: osName,
    os_version: osVersion,
    manufacturer: getUA,
    location: '',
    gps_info: 'info',
    machine_code: uniqueDeviceCode,
    application_type: 'web',
  };

  const stringifiedDeviceData = JSON.stringify(deviceData);

  setCookie(DEVICE_CODE, uniqueDeviceCode);
  setLocalStorage(DEVICE_DETAILS, stringifiedDeviceData);

  return { devicedetails: stringifiedDeviceData };
};

export const getDeviceDetails = () => {
  const localDeviceData = getLocalStorage(DEVICE_DETAILS);
  return localDeviceData ? { devicedetails: localDeviceData } : null;
};

export const ClearLocalTestCustomer = () => {
  removeCookie(TEST_CUSTOMER_TOKEN_KEY);
};

export const logoutClearLocal = () => {
  removeCookie(DEVICE_CODE);
  removeCookie(FCM_ID);
  removeCookie(VERIFY_CREDENTIALS);
  removeCookie(VAPIDKEY);
  removeCookie(TOKEN_KEY);

  ClearLocalTestCustomer();

  removeLocalStorage(DEVICE_CODE);
  removeLocalStorage(DEVICE_DETAILS);
  removeLocalStorage(FCM_ID);
  removeLocalStorage(VERIFY_CREDENTIALS);
  removeLocalStorage(VAPIDKEY);
  removeLocalStorage(TOKEN_KEY);
};
